import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';
import '../../../styles/vehicle-checklist.css';

interface VeiculoChecklistPersonalizado {
  id?: number;
  veiculo_id: number;
  nome: string;
  descricao?: string;
  ativo: boolean;
  grupos: GrupoChecklist[];
  agendamento_automatico: AgendamentoAutomatico;
  whatsapp_config: WhatsAppConfig;
  criado_em?: string;
  atualizado_em?: string;
}

interface GrupoChecklist {
  id?: number;
  nome: string;
  descricao?: string;
  ordem: number;
  itens: ItemChecklist[];
}

interface ItemChecklist {
  id?: number;
  descricao: string;
  tipo: 'texto' | 'numero' | 'sim_nao' | 'opcoes';
  obrigatorio: boolean;
  opcoes?: string[];
  ordem: number;
  observacao?: string;
}

interface AgendamentoAutomatico {
  ativo: boolean;
  dias_semana: string[];
  horarios: string[];
  proximo_envio?: string;
}

interface WhatsAppConfig {
  numero_padrao: string;
  conexao_ativa: boolean;
  mensagem_personalizada?: string;
}

const VehicleChecklistPersonalizado: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [checklist, setChecklist] = useState<VeiculoChecklistPersonalizado | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'configuracao' | 'grupos' | 'agendamento' | 'whatsapp' | 'json'>('configuracao');
  const [jsonData, setJsonData] = useState<any>(null);

  useEffect(() => {
    loadChecklist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadChecklist = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Verificar se o ID é válido (não NaN ou undefined)
      const vehicleId = Number(id);
      if (isNaN(vehicleId) || !id) {
        // Para criação de veículo, usar estado vazio
        setChecklist({
          veiculo_id: 0, // ID temporário para criação
          nome: 'Checklist Personalizado - Novo Veículo',
          ativo: true,
          grupos: [],
          agendamento_automatico: {
            ativo: false,
            dias_semana: [],
            horarios: []
          },
          whatsapp_config: {
            numero_padrao: '',
            conexao_ativa: false
          }
        });
        setIsLoading(false);
        return;
      }
      
      // Usar API adaptada que aceita estrutura do frontend
      const response = await apiService.getVeiculoChecklistAdaptadoByVehicle(vehicleId);
      
      if (response.success && response.data) {
        setChecklist(response.data);
      } else {
        // Se não existir checklist, criar um padrão
        setChecklist({
          veiculo_id: vehicleId,
          nome: `Checklist Personalizado - Veículo ${vehicleId}`,
          ativo: true,
          grupos: [],
          agendamento_automatico: {
            ativo: false,
            dias_semana: [],
            horarios: []
          },
          whatsapp_config: {
            numero_padrao: '',
            conexao_ativa: false
          }
        });
      }
    } catch (error: any) {
      console.error('Erro ao carregar checklist personalizado:', error);
      
      // Se for erro 404 (checklist não encontrado), criar um padrão
      if (error.message && error.message.includes('404')) {
        setChecklist({
          veiculo_id: Number(id),
          nome: `Checklist Personalizado - Veículo ${id}`,
          ativo: true,
          grupos: [],
          agendamento_automatico: {
            ativo: false,
            dias_semana: [],
            horarios: []
          },
          whatsapp_config: {
            numero_padrao: '',
            conexao_ativa: false
          }
        });
      } else {
        setError('Erro ao carregar checklist personalizado');
        utils.showNotification('Erro ao carregar checklist personalizado', { type: 'error' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadJsonData = async () => {
    try {
      const response = await apiService.getVeiculoChecklistJson(Number(id));
      if (response.success && response.data) {
        setJsonData(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar JSON:', error);
    }
  };

  const saveChecklist = async () => {
    if (!checklist) return;

    try {
      setIsLoading(true);
      
      // Verificar se é criação de veículo (veiculo_id = 0)
      if (checklist.veiculo_id === 0) {
        utils.showNotification('Checklist preparado para salvar com o veículo!', { type: 'success' });
        setIsLoading(false);
        return;
      }

      let response;
      if (checklist.id) {
        // Usar API adaptada para atualização
        response = await apiService.updateVeiculoChecklistAdaptado(checklist.id, checklist);
      } else {
        // Usar API adaptada para criação
        response = await apiService.createVeiculoChecklistAdaptado(checklist);
      }
      
      if (response.success) {
        utils.showNotification('Checklist salvo com sucesso!', { type: 'success' });
        loadChecklist(); // Recarregar para obter ID se for criação
      } else {
        throw new Error(response.message || 'Erro ao salvar checklist');
      }
    } catch (error: any) {
      console.error('Erro ao salvar checklist:', error);
      utils.showNotification(error.message || 'Erro ao salvar checklist', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const addGroup = () => {
    if (!checklist) return;

    const newGroup: GrupoChecklist = {
      nome: `Grupo ${checklist.grupos.length + 1}`,
      ordem: checklist.grupos.length,
      itens: []
    };

    setChecklist({
      ...checklist,
      grupos: [...checklist.grupos, newGroup]
    });
  };

  const updateGroup = (index: number, updatedGroup: GrupoChecklist) => {
    if (!checklist) return;

    const updatedGroups = [...checklist.grupos];
    updatedGroups[index] = updatedGroup;
    
    setChecklist({
      ...checklist,
      grupos: updatedGroups
    });
  };

  const removeGroup = (index: number) => {
    if (!checklist) return;

    const updatedGroups = checklist.grupos.filter((_, i) => i !== index);
    // Reordenar grupos
    const reorderedGroups = updatedGroups.map((group, i) => ({
      ...group,
      ordem: i
    }));
    
    setChecklist({
      ...checklist,
      grupos: reorderedGroups
    });
  };

  const addItemToGroup = (groupIndex: number) => {
    if (!checklist) return;

    const group = checklist.grupos[groupIndex];
    const newItem: ItemChecklist = {
      descricao: `Item ${group.itens.length + 1}`,
      tipo: 'texto',
      obrigatorio: false,
      ordem: group.itens.length
    };

    const updatedGroups = [...checklist.grupos];
    updatedGroups[groupIndex] = {
      ...group,
      itens: [...group.itens, newItem]
    };
    
    setChecklist({
      ...checklist,
      grupos: updatedGroups
    });
  };

  const updateItemInGroup = (groupIndex: number, itemIndex: number, updatedItem: ItemChecklist) => {
    if (!checklist) return;

    const updatedGroups = [...checklist.grupos];
    const group = updatedGroups[groupIndex];
    group.itens[itemIndex] = updatedItem;
    
    setChecklist({
      ...checklist,
      grupos: updatedGroups
    });
  };

  const removeItemFromGroup = (groupIndex: number, itemIndex: number) => {
    if (!checklist) return;

    const updatedGroups = [...checklist.grupos];
    const group = updatedGroups[groupIndex];
    group.itens = group.itens.filter((_, i) => i !== itemIndex);
    // Reordenar itens
    group.itens = group.itens.map((item, i) => ({
      ...item,
      ordem: i
    }));
    
    setChecklist({
      ...checklist,
      grupos: updatedGroups
    });
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-body" style={{ textAlign: 'center', padding: '50px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando checklist personalizado...</p>
        </div>
      </div>
    );
  }

  if (error || !checklist) {
    return (
      <div className="card">
        <div className="card-body" style={{ textAlign: 'center', padding: '50px 20px' }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: '#e74c3c', marginBottom: '20px' }}></i>
          <h3>{error || 'Checklist não encontrado'}</h3>
          <p>Não foi possível carregar o checklist personalizado.</p>
          <button className="btn btn-primary" onClick={loadChecklist}>
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="vehicle-checklist-personalizado">
      {/* Cabeçalho e Abas */}
      <div className="card">
        <div className="card-header">
          <h2>Checklist Personalizado</h2>
          <div className="tab-navigation">
            <button 
              className={`tab-button ${activeTab === 'configuracao' ? 'active' : ''}`}
              onClick={() => setActiveTab('configuracao')}
            >
              <i className="fas fa-cog"></i>
              Configuração
            </button>
            <button 
              className={`tab-button ${activeTab === 'grupos' ? 'active' : ''}`}
              onClick={() => setActiveTab('grupos')}
            >
              <i className="fas fa-layer-group"></i>
              Grupos e Itens
            </button>
            <button 
              className={`tab-button ${activeTab === 'json' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('json');
                loadJsonData();
              }}
            >
              <i className="fas fa-code"></i>
              JSON
            </button>
          </div>
        </div>

        <div className="card-body">
          {/* Aba de Configuração */}
          {activeTab === 'configuracao' && (
            <div className="tab-content">
              <h3>Configuração Geral</h3>
              
              <div className="form-group">
                <label className="form-label">Nome do Checklist</label>
                <input
                  type="text"
                  value={checklist.nome}
                  onChange={(e) => setChecklist({ ...checklist, nome: e.target.value })}
                  className="form-control"
                  placeholder="Nome do checklist personalizado"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Descrição</label>
                <textarea
                  value={checklist.descricao || ''}
                  onChange={(e) => setChecklist({ ...checklist, descricao: e.target.value })}
                  className="form-control"
                  placeholder="Descrição do checklist"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label className="form-checkbox">
                  <input
                    type="checkbox"
                    checked={checklist.ativo}
                    onChange={(e) => setChecklist({ ...checklist, ativo: e.target.checked })}
                  />
                  <span className="checkmark"></span>
                  Checklist Ativo
                </label>
                <small className="form-text">Desative para pausar o envio automático</small>
              </div>

              <div className="form-actions">
                <button className="btn btn-primary" onClick={saveChecklist} disabled={isLoading}>
                  {isLoading ? 'Salvando...' : 'Salvar Checklist'}
                </button>
              </div>
            </div>
          )}

          {/* Aba de Grupos e Itens */}
          {activeTab === 'grupos' && (
            <div className="tab-content">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3>Grupos e Itens do Checklist</h3>
                <button className="btn btn-primary" onClick={addGroup}>
                  <i className="fas fa-plus"></i>
                  Adicionar Grupo
                </button>
              </div>

              {checklist.grupos.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-layer-group empty-state-icon"></i>
                  <h4>Nenhum grupo criado</h4>
                  <p>Adicione grupos para organizar os itens do checklist</p>
                </div>
              ) : (
                <div className="grupos-container">
                  {checklist.grupos.map((grupo, grupoIndex) => (
                    <div key={grupoIndex} className="grupo-card">
                      <div className="grupo-header">
                        <div className="grupo-info">
                          <input
                            type="text"
                            value={grupo.nome}
                            onChange={(e) => updateGroup(grupoIndex, { ...grupo, nome: e.target.value })}
                            className="form-control"
                            placeholder="Nome do grupo"
                            style={{ fontWeight: 'bold', fontSize: '1.1rem' }}
                          />
                          <input
                            type="text"
                            value={grupo.descricao || ''}
                            onChange={(e) => updateGroup(grupoIndex, { ...grupo, descricao: e.target.value })}
                            className="form-control"
                            placeholder="Descrição do grupo"
                            style={{ marginTop: '5px' }}
                          />
                        </div>
                        <div className="grupo-actions">
                          <button 
                            className="btn btn-sm btn-primary"
                            onClick={() => addItemToGroup(grupoIndex)}
                            title="Adicionar Item"
                          >
                            <i className="fas fa-plus"></i>
                            Item
                          </button>
                          <button 
                            className="btn btn-sm btn-danger"
                            onClick={() => removeGroup(grupoIndex)}
                            title="Remover Grupo"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>

                      <div className="itens-container">
                        {grupo.itens.length === 0 ? (
                          <div className="empty-state-small">
                            <p>Nenhum item neste grupo</p>
                          </div>
                        ) : (
                          grupo.itens.map((item, itemIndex) => (
                            <div key={itemIndex} className="item-card">
                              <div className="item-header">
                                <input
                                  type="text"
                                  value={item.descricao}
                                  onChange={(e) => updateItemInGroup(grupoIndex, itemIndex, { ...item, descricao: e.target.value })}
                                  className="form-control"
                                  placeholder="Descrição do item"
                                />
                                <button 
                                  className="btn btn-sm btn-danger"
                                  onClick={() => removeItemFromGroup(grupoIndex, itemIndex)}
                                  title="Remover Item"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </div>
                              
                              <div className="item-config">
                                <div className="form-group">
                                  <label className="form-label">Tipo</label>
                                  <select
                                    value={item.tipo}
                                    onChange={(e) => updateItemInGroup(grupoIndex, itemIndex, { ...item, tipo: e.target.value as any })}
                                    className="form-control"
                                  >
                                    <option value="texto">Texto</option>
                                    <option value="numero">Número</option>
                                    <option value="sim_nao">Sim/Não</option>
                                    <option value="opcoes">Opções</option>
                                  </select>
                                </div>

                                <div className="form-group">
                                  <label className="form-checkbox">
                                    <input
                                      type="checkbox"
                                      checked={item.obrigatorio}
                                      onChange={(e) => updateItemInGroup(grupoIndex, itemIndex, { ...item, obrigatorio: e.target.checked })}
                                    />
                                    <span className="checkmark"></span>
                                    Obrigatório
                                  </label>
                                </div>

                                {item.tipo === 'opcoes' && (
                                  <div className="form-group">
                                    <label className="form-label">Opções (separadas por vírgula)</label>
                                    <input
                                      type="text"
                                      value={item.opcoes?.join(', ') || ''}
                                      onChange={(e) => updateItemInGroup(grupoIndex, itemIndex, { ...item, opcoes: e.target.value.split(',').map(op => op.trim()) })}
                                      className="form-control"
                                      placeholder="Ex: Bom, Regular, Ruim"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="form-actions" style={{ marginTop: '20px' }}>
                <button className="btn btn-primary" onClick={saveChecklist} disabled={isLoading}>
                  {isLoading ? 'Salvando...' : 'Salvar Checklist'}
                </button>
              </div>
            </div>
          )}

          {/* Aba de JSON */}
          {activeTab === 'json' && (
            <div className="tab-content">
              <h3>JSON Estruturado</h3>
              
              {jsonData ? (
                <div className="json-container">
                  <pre style={{ 
                    backgroundColor: '#f8f9fa', 
                    padding: '15px', 
                    borderRadius: '4px', 
                    border: '1px solid #ddd',
                    maxHeight: '400px',
                    overflow: 'auto'
                  }}>
                    {JSON.stringify(jsonData, null, 2)}
                  </pre>
                </div>
              ) : (
                <div className="empty-state">
                  <i className="fas fa-code empty-state-icon"></i>
                  <h4>JSON não disponível</h4>
                  <p>Salve o checklist primeiro para gerar o JSON estruturado</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehicleChecklistPersonalizado;
